import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MovingDirection, WizardComponent } from "@app/external/angular-archwizard";
import { ClientInputContentGroup } from 'client/models';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subscription, catchError, map, of } from 'rxjs';

import { generateFormGroupForInputContentGroup } from '../common/form.helper';
import { FormStore } from '../common/form.store';
import { ClientEvideFormService } from '../services/client-evide-form.service';

export type PageData = {
  inputContentGroupLists: ClientInputContentGroup[][], formGroupList: FormGroup[]
};
@Component({
  selector: 'client-form-page-steps',
  templateUrl: './client-form-page-steps.component.html',
  styleUrls: ['./client-form-page-steps.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class ClientFormPageStepsComponent implements OnInit {

  @Input() licenseId: number;
  @Input() inputContentGroupList: ClientInputContentGroup[] = [];
  @Input() preview = false;
  @Input() processing$ = new BehaviorSubject<boolean>(false);
  @Input() isValid$ = new BehaviorSubject<boolean>(false);
  @Input() disableSubmit = false;
  @Input() disableSubmitReason!: string;
  @Output() formSubmit = new EventEmitter<any>();

  public pageData!: PageData;

  public isRecaptchaEnable = false;
  public captchaSiteKeyObs!: Observable<string>;

  isSubmitted: boolean = false;

  @ViewChild(WizardComponent) public wizard: WizardComponent;
  get currentStepIndex(): number {
    return [this.wizard?.currentStepIndex, 0].includes(-1) ? 0 : (this.wizard?.currentStepIndex ?? 0);
  }

  private subs = new Subscription();

  constructor(
    private fb: FormBuilder,
    private alert: ToastrService,
    private clientFormService: ClientEvideFormService
  ) { }

  ngOnInit() {
    this.isRecaptchaEnable = !this.preview;
    this.pageData = this.generatePageForms(this.inputContentGroupList);
    if (this.isRecaptchaEnable) {
      this.getRecaptchaSiteKey();
    }
    const sub = FormStore.reset$.subscribe(() => this.wizard.reset());
    this.subs.add(sub);
  }

  private generatePageForms(inputContentGroupList: ClientInputContentGroup[]): PageData {
    let currentForm = this.fb.group({});
    let currentGroup: ClientInputContentGroup[] = [];
    const pageData: PageData = { formGroupList: [], inputContentGroupLists: [] };

    inputContentGroupList.forEach((group, index) => {
      if (index === 0 || group.pageBreak) {
        currentForm = this.fb.group({});
        currentGroup = [];
        pageData.formGroupList.push(currentForm);
        pageData.inputContentGroupLists.push(currentGroup);
      }
      currentForm.addControl(group.uuid, generateFormGroupForInputContentGroup(this.fb, group));
      currentGroup.push(group);
    });
    if (this.isRecaptchaEnable) {
      currentForm.addControl('recaptcha', new FormControl(['', Validators.required]));
    }
    if (!pageData.formGroupList.length) {
      pageData.formGroupList.push(currentForm);
    }
    return pageData;
  }

  private getRecaptchaSiteKey() {
    this.captchaSiteKeyObs = this.clientFormService.getCaptchaInfo().pipe(
      map(res => res.dataSiteKey),
      catchError(this.onGetError.bind(this))
    );
  }

  private onGetError() {
    this.isValid$.next(false);
    return of();
  }

  public submit() {
    if (this.processing$.value) {
      return;
    }

    this.pageData.formGroupList.forEach(form => form.markAllAsTouched());
    FormStore.touched$.next();
    FormStore.submitted$.next(true);

    const invalidForm = this.pageData.formGroupList.some(form => form.invalid);
    if (invalidForm) {
      this.alert.error('Please fill form correctly!');
      const errorControls = this.getInvalidInputContent();
      console.error('Invalid form - ', this.pageData.formGroupList)
      console.error("List of contols which are invalid - ", errorControls);
    }

    let catchaValidatoinFail = false;
    if (this.isRecaptchaEnable) {
      const recaptchaForm = this.pageData.formGroupList.last();
      if (recaptchaForm.get('recaptcha')?.invalid) {
        this.alert.error('Please validate captcha!');
        catchaValidatoinFail = true;
      }
    }

    if (invalidForm || catchaValidatoinFail) {
      this.formSubmit.emit(false);
      return
    }


    const rawValue = {};
    this.pageData.formGroupList.forEach(form => Object.assign(rawValue, form.getRawValue()));
    // Carry the reference-field state alongside the answers (like the recaptcha key). Untouched → the server
    // allocates the real gap-free sequential reference on submit; user-edited → honour their typed value.
    (rawValue as any).forceGenerateReference = this.isReferenceFieldPristine();
    this.formSubmit.emit(rawValue);
  }

  // The reference field is pre-filled with a provisional value via patchValue (which does NOT mark it dirty),
  // so control.pristine reliably distinguishes "kept the provisional" from "user typed their own". Returns
  // true when the field is untouched or absent (server generates), false when the user edited it.
  private isReferenceFieldPristine(): boolean {
    for (let pageIndex = 0; pageIndex < this.pageData.inputContentGroupLists.length; pageIndex++) {
      const formGroup = this.pageData.formGroupList[pageIndex];
      for (const group of this.pageData.inputContentGroupLists[pageIndex]) {
        const groupControl = formGroup.get(group.uuid) as FormGroup;
        if (!groupControl) { continue; }
        for (const inputContent of group.inputContents) {
          if (inputContent.isReferenceField) {
            const control = groupControl.get(inputContent.inputContentGuid);
            return control ? control.pristine : true;
          }
        }
      }
    }
    return true;
  }

  public next() {
    this.isSubmitted = true;
    if (!this.canExit(MovingDirection.Forwards)) {
      const inputContents = this.inputContentGroupList.flatMap(group => group.inputContents);
      this.alert.error('Please fill form correctly!');
      console.error("List of contols which are invalid for this step - ", this.findInvalidControls(this.pageData.formGroupList[this.currentStepIndex], inputContents));
    }
  }

  public canExit(direction: MovingDirection) {
    if (direction === MovingDirection.Backwards) {
      return true;
    }
    if (this.isSubmitted) {
      this.pageData.formGroupList[this.currentStepIndex].markAllAsTouched();
      FormStore.touched$.next();
    }
    if (!this.pageData.formGroupList[this.currentStepIndex]?.invalid) {
      this.isSubmitted = false;
    }
    return !this.pageData.formGroupList[this.currentStepIndex]?.invalid;
  }

  public reset() {
    FormStore.reset$.next();
    FormStore.submitted$.next(false);
    this.pageData.formGroupList.forEach(x => {
      x.reset();
    });
  }

  private findInvalidControls = (formGroup: FormGroup, inputContents) => {
    const errorControls: { inputContentGuid: string, inputContent: any, control: FormControl }[] = [];
    if (!formGroup || !formGroup.controls) return errorControls;

    Object.entries(formGroup.controls).forEach(([formControlKey, formControl]) => {
      if (formControl instanceof FormGroup) {
        // Recursively process nested FormGroups
        errorControls.push(...this.findInvalidControls(formControl, inputContents));
      } else if (formControl instanceof FormControl && formControl.errors) {
        // Match inputContent by guid and collect invalid control
        const matchingInputContent = inputContents.find(inputContent => inputContent.inputContentGuid === formControlKey);
        errorControls.push({
          control: formControl,
          inputContentGuid: formControlKey,
          inputContent: matchingInputContent
        });
      }
    });

    return errorControls;
  };

  private getInvalidInputContent() {
    const errorControls: { inputContentGuid: string, inputContent: any, control: FormControl }[] = [];
    const inputContents = this.inputContentGroupList.flatMap(group => group.inputContents);

    // Iterate over all form groups in the formGroupList
    this.pageData.formGroupList.forEach(formGroup => {
      if (formGroup instanceof FormGroup) {
        errorControls.push(...this.findInvalidControls(formGroup, inputContents));
      }
    });

    return errorControls;
  }


}
